// A18 — Xavfsizlik va audit (2-to'plam, 14 ekran). Module-local data layer
// (does NOT touch `lib/adapters.ts` / `i18n/*` — HOT). REAL endpoints reused
// where they exist (incidents A17, antifraud A25); fetchers fail-soft to empty
// where the backend route is greenfield/external-tooling (DDoS/WAF/pentest/…).
// Registration = ./REGISTER.md (routes/screenStatus/nav/i18n applied elsewhere).
import { api } from '@/lib/api'
import type { FetchParams, FetchResult } from '@/components/DataTable/types'

function toResult<T>(
  rows: T[],
  params: FetchParams,
  search?: (r: T) => string,
): FetchResult<T> {
  const q = params.search.trim().toLowerCase()
  const filtered = q && search ? rows.filter((r) => search(r).toLowerCase().includes(q)) : rows
  const start = params.page * params.pageSize
  return { rows: filtered.slice(start, start + params.pageSize), total: filtered.length }
}

async function safeList<T>(path: string, key: string, signal: AbortSignal): Promise<T[]> {
  try {
    const data = await api.get<Record<string, T[]>>(path, { signal })
    return (data?.[key] as T[] | undefined) ?? []
  } catch {
    return []
  }
}

/* ─────────────────────────── A18.09 — Xavfsizlik incidentlari (REAL) ──────
 * Reuses the live A17 incident feed `GET /admin/incidents` (newest first). */
export interface IncidentRow {
  id: number
  title: string
  severity: string
  status: string
  summary: string | null
  started_at: string
  author: string | null
}

export function fetchIncidents(params: FetchParams): Promise<FetchResult<IncidentRow>> {
  return safeList<IncidentRow>('/admin/incidents', 'incidents', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.title} ${r.summary ?? ''} ${r.severity}`),
  )
}

/* ─────────────────────────── A18.11 — Anti-fraud qoidalari (REAL) ─────────
 * Live `GET /admin/antifraud/rules` + `POST .../rules/:id/toggle {enabled}`. */
export interface FraudRuleRow {
  id: number
  name: string
  condition: string
  action: 'flag' | 'block' | 'review'
  enabled: boolean
  hits: number
}

export function fetchFraudRules(params: FetchParams): Promise<FetchResult<FraudRuleRow>> {
  return safeList<FraudRuleRow>('/admin/antifraud/rules', 'rules', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.name} ${r.condition}`),
  )
}

export function toggleFraudRule(id: number, enabled: boolean): Promise<{ enabled: boolean }> {
  return api.post<{ enabled: boolean }>(`/admin/antifraud/rules/${id}/toggle`, { enabled })
}

/* ─────────────────────────── A18.12 — Firibgarlik holatlar (REAL) ─────────
 * Live `GET /admin/antifraud/payments` + `.../payments/:id/{confirm|clear}`. */
export interface PaymentFraudRow {
  id: number
  user: string
  amount: number
  method: string
  reason: string
  created_at: string
  status: 'open' | 'confirmed' | 'cleared'
}

export function fetchFraudCases(params: FetchParams): Promise<FetchResult<PaymentFraudRow>> {
  return safeList<PaymentFraudRow>('/admin/antifraud/payments', 'events', params.signal).then(
    (rows) => toResult(rows, params, (r) => `${r.user} ${r.method} ${r.reason}`),
  )
}

export function resolveFraudCase(
  id: number,
  action: 'confirm' | 'clear',
): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/antifraud/payments/${id}/${action}`, {})
}

/* ─────────────────────────── A18.13 — Anomaliya aniqlash (REAL) ───────────
 * Live scored-anomaly feed `GET /admin/antifraud/events` + block/clear. */
export interface AnomalyRow {
  id: number
  user: string
  type: string
  score: number
  reason: string
  created_at: string
  status: 'open' | 'blocked' | 'cleared'
}

export function fetchAnomalies(params: FetchParams): Promise<FetchResult<AnomalyRow>> {
  return safeList<AnomalyRow>('/admin/antifraud/events', 'events', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.user} ${r.type} ${r.reason}`),
  )
}

export function resolveAnomaly(id: number, action: 'block' | 'clear'): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/antifraud/events/${id}/${action}`, {})
}

/* ─────────────────────────── A18.14 — Bot fermalari (REAL) ────────────────
 * Live device-fingerprint feed `GET /admin/antifraud/devices` — one device
 * shared by many accounts = a bot farm signal. */
export interface DeviceRow {
  id: string
  platform: string
  accounts: number
  first_seen: string
  last_seen: string
  flagged: boolean
}

export function fetchBotFarms(params: FetchParams): Promise<FetchResult<DeviceRow>> {
  return safeList<DeviceRow>('/admin/antifraud/devices', 'devices', params.signal).then((rows) =>
    // Sort accounts-desc: densest fingerprints (most likely farms) surface first.
    toResult(
      [...rows].sort((a, b) => Number(b.accounts) - Number(a.accounts)),
      params,
      (r) => `${r.id} ${r.platform}`,
    ),
  )
}

/* ─────────────────────────── A18.19 — IP qora/oq ro'yxatlari (REAL) ───────
 * Live `GET /admin/antifraud/blacklist` + `DELETE .../blacklist/:id`. */
export interface BlacklistRow {
  id: number
  kind: 'ip' | 'device' | 'phone' | 'email' | 'card'
  value: string
  reason: string
  added_at: string
}

export function fetchIpLists(params: FetchParams): Promise<FetchResult<BlacklistRow>> {
  return safeList<BlacklistRow>('/admin/antifraud/blacklist', 'entries', params.signal).then(
    (rows) => toResult(rows, params, (r) => `${r.value} ${r.reason} ${r.kind}`),
  )
}

export function removeIpEntry(id: number): Promise<{ ok: boolean }> {
  return api.del<{ ok: boolean }>(`/admin/antifraud/blacklist/${id}`)
}

/* ─────────────────────────── A18.15 — Ommaviy ro'yxat hujumi (fail-soft) ──
 * `/admin/security/mass-registration` greenfield → empty until it ships. */
export interface MassRegRow {
  id: number
  window: string
  ip: string
  count: number
  pattern: string
  status: 'open' | 'mitigated' | 'cleared'
  created_at: string
}

export function fetchMassRegistration(params: FetchParams): Promise<FetchResult<MassRegRow>> {
  return safeList<MassRegRow>('/admin/security/mass-registration', 'waves', params.signal).then(
    (rows) => toResult(rows, params, (r) => `${r.ip} ${r.pattern}`),
  )
}

/* ─────────────────────────── A18.16 — Kredensial stuffing (fail-soft) ─────
 * `/admin/security/credential-stuffing` greenfield → empty until it ships. */
export interface CredStuffRow {
  id: number
  target: string
  ip: string
  attempts: number
  failed: number
  window: string
  status: 'open' | 'mitigated' | 'cleared'
}

export function fetchCredentialStuffing(params: FetchParams): Promise<FetchResult<CredStuffRow>> {
  return safeList<CredStuffRow>(
    '/admin/security/credential-stuffing',
    'attempts',
    params.signal,
  ).then((rows) => toResult(rows, params, (r) => `${r.target} ${r.ip}`))
}

/* ─────────────────────────── A18.24 — Xavfsizlik siyosatlari (fail-soft) ──
 * `/admin/security/policies` greenfield (settings surface) → empty for now. */
export interface PolicyRow {
  id: string
  name: string
  category: string
  enforced: boolean
  scope: string
  updated_at: string
}

export function fetchSecurityPolicies(params: FetchParams): Promise<FetchResult<PolicyRow>> {
  return safeList<PolicyRow>('/admin/security/policies', 'policies', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.name} ${r.category}`),
  )
}

/* ═══════════════════════════ A18 admin-managed registries (REAL CRUD) ═════
 * DDoS-rules / WAF / vuln-scans / dependency-audit / pentest are now backed by
 * admin-curated registries (`sec_*` tables, migration 000248) — the admin owns
 * the ruleset/result list, so they are functional with no external tool wired.
 * Endpoints: GET/POST/PUT/DELETE /admin/security/{ddos-rules|waf-rules|
 * vuln-scans|dep-audit|pentest}. Generic list/create/update/delete helpers below. */

/** Minimal shape — every registry row carries `id` (backend-assigned). */
export interface SecRow {
  id: number | string
  [key: string]: unknown
}

/** Ro'yxat fetcher fabrikasi — `searchKeys` tez qidiruvda solishtiriladi. */
export function secList<T extends SecRow>(
  path: string,
  key: string,
  searchKeys: string[],
): (params: FetchParams) => Promise<FetchResult<T>> {
  return (params: FetchParams) =>
    safeList<T>(path, key, params.signal).then((rows) =>
      toResult(rows, params, (r) => searchKeys.map((k) => String(r[k] ?? '')).join(' ')),
    )
}

/** Bir martalik ro'yxat (DDoS status paneli qatorlarni to'g'ridan-to'g'ri o'qiydi). */
export function secListOnce<T>(path: string, key: string, signal: AbortSignal): Promise<T[]> {
  return safeList<T>(path, key, signal)
}

export function secCreate(
  path: string,
  body: Record<string, unknown>,
): Promise<{ ok: boolean; id?: number }> {
  return api.post<{ ok: boolean; id?: number }>(path, body)
}

export function secUpdate(
  path: string,
  id: number | string,
  body: Record<string, unknown>,
): Promise<{ ok: boolean }> {
  return api.put<{ ok: boolean }>(`${path}/${encodeURIComponent(String(id))}`, body)
}

export function secDelete(path: string, id: number | string): Promise<{ ok: boolean }> {
  return api.del<{ ok: boolean }>(`${path}/${encodeURIComponent(String(id))}`)
}

/* A18.17 — DDoS rate/threshold rule config. `/admin/security/ddos-rules`. */
export interface DdosRuleRow extends SecRow {
  id: number
  rule: string
  threshold: number
  action: 'block' | 'challenge' | 'throttle' | 'monitor'
  scope: string
  enabled: boolean
  updated_at: string
}

/* A18.18 — WAF ruleset. `/admin/security/waf-rules`. */
export interface WafRuleRow extends SecRow {
  id: number
  name: string
  pattern: string
  action: 'block' | 'challenge' | 'log'
  enabled: boolean
  updated_at: string
}

/* A18.20 — logged vulnerability-scan results. `/admin/security/vuln-scans`. */
export interface VulnScanRow extends SecRow {
  id: number
  target: string
  scanner: string
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info'
  status: 'open' | 'triaged' | 'fixed' | 'accepted'
  notes: string
  found_at: string
}

/* A18.21 — dependency-audit registry. `/admin/security/dep-audit`. */
export interface DepAuditRow extends SecRow {
  id: number
  package: string
  version: string
  cve: string
  severity: 'critical' | 'high' | 'medium' | 'low'
  fixed_in: string
  status: 'open' | 'patched' | 'ignored'
  updated_at: string
}

/* A18.22 — pentest findings registry. `/admin/security/pentest`. */
export interface PentestRow extends SecRow {
  id: number
  title: string
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info'
  component: string
  status: 'open' | 'fixed' | 'accepted'
  notes: string
  found_at: string
}

/** Module-local RU strings (i18n HOT → keyга ko'chiriladi, REGISTER.md). */
export const SEC_STR = {
  // shared
  status: 'Статус',
  created: 'Когда',
  reason: 'Причина',
  user: 'Пользователь',
  block: 'Заблокировать',
  clear: 'Очистить (не фрод)',
  failed: 'Не удалось выполнить',
  blocked: 'Заблокировано',
  cleared: 'Очищено',
  openLbl: 'Открыто',
  // A18.09 incidents
  incidents: 'Инциденты безопасности',
  title: 'Заголовок',
  severity: 'Критичность',
  summary: 'Описание',
  startedAt: 'Начат',
  author: 'Автор',
  // A18.11 fraud rules
  fraudRules: 'Правила анти-фрода',
  ruleName: 'Правило',
  condition: 'Условие',
  action: 'Действие',
  enabledLbl: 'Вкл.',
  hits: 'Срабатываний',
  enable: 'Включить',
  disable: 'Выключить',
  toggled: 'Правило обновлено',
  // A18.12 fraud cases
  fraudCases: 'Случаи мошенничества',
  amount: 'Сумма',
  method: 'Способ',
  confirm: 'Подтвердить фрод',
  confirmed: 'Отмечено как фрод',
  // A18.13 anomaly
  anomaly: 'Обнаружение аномалий',
  type: 'Тип',
  score: 'Балл',
  // A18.14 bot farms
  botFarms: 'Бот-фермы',
  device: 'Устройство',
  platform: 'Платформа',
  accounts: 'Аккаунтов',
  firstSeen: 'Первый раз',
  lastSeen: 'Последний раз',
  flagged: 'Помечено',
  // A18.15 mass registration
  massReg: 'Атака массовой регистрации',
  window: 'Окно',
  ip: 'IP',
  count: 'Кол-во',
  pattern: 'Шаблон',
  // A18.16 credential stuffing
  credStuffing: 'Credential stuffing',
  target: 'Цель',
  attempts: 'Попыток',
  failedCount: 'Неудачных',
  // A18.17 ddos
  ddos: 'DDoS-атаки',
  rps: 'RPS',
  peakRps: 'Пик RPS',
  // A18.18 waf
  waf: 'Правила WAF',
  wafPattern: 'Паттерн',
  // A18.19 ip lists
  ipLists: 'IP чёрный/белый списки',
  kind: 'Тип',
  value: 'Значение',
  added: 'Добавлен',
  remove: 'Удалить',
  removed: 'Запись удалена',
  // A18.20 vuln scans
  vulnScans: 'Сканеры уязвимостей',
  scanner: 'Сканер',
  critical: 'Критич.',
  high: 'Высокие',
  medium: 'Средние',
  ranAt: 'Запущен',
  // A18.21 dependency audit
  depAudit: 'Аудит зависимостей',
  pkg: 'Пакет',
  version: 'Версия',
  advisory: 'Бюллетень',
  fixedIn: 'Исправлено в',
  // A18.22 pentest
  pentest: 'Пентест',
  component: 'Компонент',
  foundAt: 'Найдено',
  // A18.24 policies
  policies: 'Политики безопасности',
  policyName: 'Политика',
  category: 'Категория',
  enforced: 'Применяется',
  scope: 'Область',
  updated: 'Обновлено',
  // A18 CRUD registrlari (000248)
  rule: 'Правило',
  threshold: 'Порог',
  notes: 'Заметки',
  cve: 'CVE',
  edit: 'Изменить',
  save: 'Сохранить',
  cancel: 'Отмена',
  del: 'Удалить',
  low: 'Низкие',
  info: 'Инфо',
  createdGeneric: 'Создано',
  savedGeneric: 'Сохранено',
  deletedGeneric: 'Удалено',
  addDdos: 'Добавить правило',
  addWaf: 'Добавить правило WAF',
  addVuln: 'Записать результат',
  addDep: 'Добавить пакет',
  addPentest: 'Добавить находку',
  editEntry: 'Изменить запись',
  // DDoS status paneli
  panelTotal: 'Всего правил',
  panelActive: 'Активных',
  panelBlocking: 'Блокирующих',
  panelMaxThreshold: 'Макс. порог',
} as const
